import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { EnvState } from 'app/modules/shared/helpers/env-state';
import {
  AuthInfo,
  AuthRepository,
  Office365Settings,
  getStoredToken,
} from 'app/state/auth.repository';
import { AuthService } from 'app/state/auth.service';
import { HttpClient } from '@angular/common/http';
import { getTenantsAsDataList } from 'app/helpers/arrayToDataListOptonConverter';
import { GeneralDataRepository } from '../../../../helpers/repository/general-data.repository';
import { GdprConfirmationComponent } from 'app/modules/shared/components/gdpr-confirmation/gdpr-confirmation.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatalistOption } from 'app/state/abstract/base.repository';
import { UsersService } from 'app/api/services/users.service';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { TrackRender } from 'app/shared/front-perfomance';
import { TranslocoService } from '@jsverse/transloco';

@TrackRender()
@Component({
    selector: 'app-active-tenant-page',
    templateUrl: './active-tenant-page.component.html',
    styleUrls: ['./active-tenant-page.component.scss'],
    standalone: false
})
export class ActiveTenantPageComponent implements OnInit {
  title: any;
  error: string | null = null;
  testsLocation = 'localhost:9876';
  settings$: Observable<Office365Settings> | null = null;
  tenantsOptions: Array<DatalistOption> = [];
  userExists = false;
  enable365 = false;
  loginForm = this.formBuilder.group({
    tenantId: [''],
  });
  URL: string = '';
  Scope: string = '';
  ClientID: string = '';
  showPageContent: boolean = false;
  isSuperAdmin: boolean = false;

  constructor(
    private auth: AuthService,
    public repo: AuthRepository,
    private modalService: NgbModal,
    public router: Router,
    private formBuilder: UntypedFormBuilder,
    public env: EnvState,
    private http: HttpClient,
    private generalDataRepo: GeneralDataRepository,
    public userservice: UsersService,
    private tenantService: LocalStorageService,
    private transloco: TranslocoService,
  ) {
    this.loadSettings();
  }

  private baseUrlPattern = /^https:\/\/nybolig\.igntr\.me(\/.*)?$/;

  ngOnInit(): void {
    this.enable365 = this.baseUrlPattern.test(document.location.href);
    this.getUserInfo();
    //this.generalDataRepo.updateAllGeneralData(true);
  }

  chooseRedirectAction(responce: AuthInfo) {
    if (responce.tenants.length == 0) {
      this.deepLinkNavigation();
    } else if (responce.tenants.length == 1) {
      this.setActiveTenant(this.tenantsOptions[0].value);
    } else if (responce.isSuperadmin && responce.tenants.length == 0) {
      this.deepLinkNavigation();
    } else {
      this.showPageContent = true;
    }
  }

  deepLinkNavigation() {
    this.generalDataRepo.updateAllGeneralData(true);
    this.repo.returnUrl$?.subscribe((url) => {
      this.router.navigate([url]).then((x) => {
        window.location.reload();
      });
    });

    if (this.repo.returnUrl$ == undefined) {
      this.router.navigate(['/home']).then((x) => {
        window.location.reload();
      });
    }
  }

  updateValue(control: string, value: any) {
    const controlObject = this.loginForm?.get(control);
    controlObject?.setValue(value);
    controlObject?.markAsTouched();
  }

  getUserInfo() {
    this.auth.getUserInfo().subscribe((responce) => {
      if (responce) {
        if (!responce.gdprConfirmed) {
          this.showGdprConfirmation();
        }
        // Only seed the language from the server profile if the user hasn't
        // chosen one yet — otherwise the language switcher's selection on
        // the signin page gets clobbered every time we land here.
        if (!localStorage.getItem('curUserLang')) {
          localStorage.setItem('curUserLang', responce.language);
        }
        this.isSuperAdmin = responce.isSuperadmin;
        // getTenantsAsDataList drops everything except label/value, so re-join
        // the per-tenant stats (member/active counts, last activity) by id.
        this.tenantsOptions = getTenantsAsDataList(responce.tenants).map((opt) => {
          const tenant = responce.tenants.find((t) => t.id === opt.value);
          return {
            ...opt,
            memberCount: tenant?.countOfUsers ?? 0,
            activeCount: tenant?.activeUserCount ?? 0,
            lastActivity: tenant?.lastActivity ?? null,
          };
        });
        // Logout wipes activeTenantId from localStorage, but the server still
        // remembers the user's last-active tenant. Re-hydrate it so the
        // "Pinned" slot stays correct across sign-out instead of falling back
        // to the alphabetically-first tenant.
        if (
          responce.activeTenantId &&
          this.tenantsOptions.some((o) => o.value === responce.activeTenantId)
        ) {
          localStorage.setItem('activeTenantId', responce.activeTenantId);
        }
        // Default selection to the user's previously-active tenant.
        const stored = localStorage.getItem('activeTenantId');
        this.selectedTenantId =
          (stored && this.tenantsOptions.some((o) => o.value === stored)
            ? stored
            : this.tenantsOptions[0]?.value) ?? null;
        this.chooseRedirectAction(responce);
      }
    });
  }

  setActiveTenant(tenantId: string): void {
    let token = getStoredToken();
    // Switch server-side first; only mirror into client state on success so a
    // failed request can't desync the UI onto a tenant the backend isn't
    // scoped to (which shows the previous tenant's data).
    this.auth.setActiveTenant(token!, tenantId).subscribe({
      next: () => {
        localStorage.setItem('activeTenantId', tenantId);
        this.tenantService.setTenantId(tenantId);
        this.deepLinkNavigation();
      },
      error: (err) => {
        console.error('Failed to switch tenant', err);
      },
    });
  }

  skip() {
    this.deepLinkNavigation();
  }

  showGdprConfirmation() {
    const isImpersonationMode = localStorage.getItem('isImpersonatingMode');
    if (isImpersonationMode == undefined || isImpersonationMode == 'false') {
      const modalRef = this.modalService.open(GdprConfirmationComponent, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'modal-dialog-centered custom-modal',
      });
    }
  }

  loadSettings() {
    this.settings$ = this.http.get<Office365Settings>('/api/auth/office');
    this.http.get<Office365Settings>('/api/auth/office').subscribe((x) => {
      this.ClientID = x.clientId;
      this.Scope = x.scope;
      this.URL = x.url;
    });
  }

  /** Bound to the search input — used to filter the tenant lists below. */
  searchQuery = '';

  /** Currently signed-in user's email, used by the "Logged in as" header. */
  get currentUserEmail(): string {
    return this.repo.getUser()?.email ?? '';
  }

  /** First letter of first + last name (e.g., "AKA" for Allan Kirkeby Andreasen)
   * for the avatar tile. Falls back to the email initial if no name is set. */
  get currentUserInitials(): string {
    const u = this.repo.getUser();
    if (!u) return '?';
    const parts: string[] = [];
    if (u.name) parts.push(u.name);
    if (u.surname) parts.push(u.surname);
    if (parts.length === 0 && u.email) {
      return u.email.charAt(0).toUpperCase();
    }
    // Use up to 3 initials so "Head Administrator" → "HA", "Allan Kirkeby
    // Andreasen" → "AKA" if it's split across name+surname.
    const joined = parts.join(' ').trim();
    const words = joined.split(/\s+/).slice(0, 3);
    return words.map((w) => w.charAt(0).toUpperCase()).join('');
  }

  /**
   * Tenant chosen by clicking a card. Confirming via "Continue to IGNTR"
   * calls `setActiveTenant(selectedTenantId)`. Clicking a card never
   * navigates on its own — that was the previous behaviour and it was
   * surprising.
   */
  selectedTenantId: string | null = null;

  /**
   * The tenant shown in the "Pinned" section. Prefers the user's
   * previously-active tenant (from localStorage), falling back to the first
   * option in the list when there's no prior selection.
   */
  get pinnedTenant(): DatalistOption {
    const stored = localStorage.getItem('activeTenantId');
    const match = stored
      ? this.tenantsOptions.find((o) => o.value === stored)
      : undefined;
    return match ?? this.tenantsOptions[0];
  }

  /** Every tenant except the pinned one — populates the "All organisations" list. */
  get otherTenants(): DatalistOption[] {
    const pinned = this.pinnedTenant;
    const query = this.searchQuery.trim().toLowerCase();
    return this.tenantsOptions
      .filter((o) => o.value !== pinned?.value)
      .filter((o) => !query || o.label.toLowerCase().includes(query));
  }

  /** Returns the pinned tenant if it matches the current search, else undefined. */
  get visiblePinnedTenant(): DatalistOption | undefined {
    const query = this.searchQuery.trim().toLowerCase();
    const pinned = this.pinnedTenant;
    if (!pinned) return undefined;
    if (!query) return pinned;
    return pinned.label.toLowerCase().includes(query) ? pinned : undefined;
  }

  /** Total number of tenants that match the current search. */
  get visibleCount(): number {
    return (this.visiblePinnedTenant ? 1 : 0) + this.otherTenants.length;
  }

  /** First letter (or pair of letters) of a tenant name, for the avatar tile. */
  initials(label: string | undefined | null): string {
    if (!label) return '?';
    const words = label.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Deterministic gradient for tenant avatars. Hashing the label means each
   * tenant gets a consistent color across reloads without backend support.
   */
  avatarGradient(label: string | undefined | null): string {
    if (!label) return 'linear-gradient(135deg, #94a3b8, #475569)';
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = (hash * 31 + label.charCodeAt(i)) & 0xffffffff;
    }
    const hue = Math.abs(hash) % 360;
    return `linear-gradient(135deg, oklch(0.62 0.15 ${hue}), oklch(0.48 0.18 ${hue}))`;
  }

  /**
   * Human-friendly "last accessed" string for a tenant card. Mirrors the
   * original design ("today 10:42", "yesterday 09:15") and falls back to a
   * short date for anything older. Returns the translated "never" when the
   * tenant has no recorded sign-in.
   */
  formatLastActivity(value: string | null | undefined): string {
    if (!value) return this.transloco.translate('neverLoggedIn');
    const date = new Date(value);
    if (isNaN(date.getTime())) return this.transloco.translate('neverLoggedIn');

    const now = new Date();
    const time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    if (sameDay(date, now)) {
      return `${this.transloco.translate('todayAt')} ${time}`;
    }
    if (sameDay(date, yesterday)) {
      return `${this.transloco.translate('yesterdayAt')} ${time}`;
    }
    return date.toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
}
